import React, { useState } from 'react';
import { createHistory, updateHistorySetting } from '../services/historyService';
import { getDisplayErrorMessage } from '../services/apiError';
import { useNavigate } from 'react-router-dom';
import HistorySettingsPanel from './HistorySettingsPanel';
import CloseButton from './CloseButton';
import RichTextEditor from './RichTextEditor';
import ErrorMessage from './ErrorMessage';

const CreateHistoryForm = () => {
  const [name, setName] = useState('');
  const [isPublic, setIsPublic] = useState(false);
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    name: ''
  });
  const [pendingSettings, setPendingSettings] = useState({});
  const navigate = useNavigate();

  const handleSettingsChange = (key, value) => {
    setPendingSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault(); 
    
    setError(null);
    setErrors({});
    setSuccessMessage(null);
    
    const validationErrors = {};

    if (name.trim() === "") {
        validationErrors.name = "Name is required";
    }

    if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
    }
    
    setIsLoading(true);

    try {
      const response = await createHistory({ name, isPublic, description });

      if (response && response.id) {
        for (const [key, value] of Object.entries(pendingSettings)) {
          await updateHistorySetting(response.id, key, value);
        }

        navigate(`/createEvent/${response.id}`, { replace: true });
      }
    } catch (err) {
      if (err.message && err.message.includes("UNIQUE constraint failed")) {
        setError("History with this name already exists");
      } else {
        setError(getDisplayErrorMessage(err));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const validateField = (value) => {
    let error = "";
    
    if (value.trim() === "") {
      error = "Name is required";
    }

    setErrors(prev => ({ ...prev, name: error }));
  };

  return (
    <div className="h-[calc(100vh-64px)] overflow-y-auto flex items-start justify-center bg-gray-100 py-8">
      <div className="bg-gray-200 mx-auto w-full max-w-2xl rounded shadow-md py-6 relative">
      <ErrorMessage message={error} onDismiss={() => setError(null)} />
      {successMessage && (
        <div 
          className="w-full bg-green-100 text-green-500 text-center absolute shadow-2xl p-4 py-8 border-green-400 border-[1px] rounded-xl top-10 right-auto left-auto hover:cursor-pointer"
          onClick={ () => setSuccessMessage(null)}
        >
        {successMessage}
        </div>
      )}
      
        <div className="flex px-4 justify-end">
          <CloseButton onClick={() => navigate(-1)} disabled={isLoading}/>
        </div>

        <form className="bg-gray-300 rounded px-16 pt-6 pb-8 my-4 flex flex-col shadow-lg" onSubmit={ handleSubmit }>
          <h1 className="text-center text-3xl mb-6 bg-gray-200 border-2 border-gray-400 rounded-xl py-3">Creating history</h1>
          <div className="mb-4">
            <input 
              placeholder="Enter a name for history" 
              type="text" 
              className="w-full rounded p-1" 
              value={ name } 
              onChange={ (e) => setName(e.target.value) }
              onBlur={ (e) => validateField(e.target.value)}
            />
            {errors.name && <p className="text-red-500 text-xs">{errors.name}</p>}
          </div>
          
          <div className="mb-4">
            <RichTextEditor
              content={description}
              onChange={setDescription}
              placeholder="Enter description for history"
            />
          </div>
          <div className="mb-6">
            <input 
              type="checkbox" 
              className="mr-2" 
              checked={ isPublic } 
              onChange={ (e) => setIsPublic(e.target.checked) }
              id="public-check"
            />
          <label htmlFor="public-check">Public?</label>
          </div>
          <button
            disabled={ isLoading }
            type="submit" className="bg-gray-200 rounded p-2 hover:bg-blue-100 hover:border-blue-500 border-2 400 disabled:opacity-50 disabled:cursor-not-allowed"
          >
          {isLoading ? 'Processing...' : 'Create history'}
          </button>
        </form>

        <HistorySettingsPanel onSettingsChange={handleSettingsChange} />
      </div>
    </div>
  )
}

export default CreateHistoryForm;